import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ShoppingBag, MagnifyingGlass, MessengerLogo, WhatsappLogo, Tag } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const CatalogPublic = () => {
  const { businessId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const res = await fetch(`${API}/api/catalog/public/${businessId}`);
        if (!res.ok) throw new Error('Not found');
        const json = await res.json();
        setData(json);
      } catch {
        setData(null);
      }
      setLoading(false);
    };
    fetchCatalog();
  }, [businessId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1D4ED8] border-t-transparent"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <p className="text-slate-400">Catalogo no encontrado</p>
      </div>
    );
  }

  const categories = ['Todos', ...Object.keys(data.categories || {})];
  const filtered = data.products.filter(p => {
    const matchesSearch = !search ||
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.description?.toLowerCase().includes(search.toLowerCase()) ||
      p.category?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = selectedCategory === 'Todos' || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-[#0F172A]">
      {/* Header */}
      <div className="bg-[#111111] text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-2">
            <ShoppingBag size={32} weight="bold" className="text-[#1D4ED8]" />
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight" data-testid="catalog-business-name">
              {data.business.name}
            </h1>
          </div>
          {data.business.description && (
            <p className="text-sm text-slate-600 max-w-xl">{data.business.description}</p>
          )}
          <p className="text-xs text-slate-500 mt-2">{data.total} productos disponibles</p>
        </div>
      </div>

      {/* Search + Categories */}
      <div className="max-w-6xl mx-auto px-4 py-4 space-y-3">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar productos..."
            className="w-full pl-10 pr-4 py-2.5 border border-white/10 rounded-[12px] text-sm focus:ring-2 focus:ring-[#1D4ED8] focus:outline-none bg-[#111827]"
            data-testid="catalog-search"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1.5 text-xs font-bold rounded-[12px] whitespace-nowrap transition-colors ${
                selectedCategory === cat
                  ? 'bg-[#1D4ED8] text-white'
                  : 'bg-[#111827] border border-white/10 text-slate-400 hover:bg-[#0F172A]'
              }`}
              data-testid={`catalog-category-${cat}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Products Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-8">
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm text-slate-400">No se encontraron productos</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(product => (
              <div key={product.id} className="bg-[#111827] border border-white/10 rounded-[12px] overflow-hidden hover:shadow-md transition-shadow" data-testid={`catalog-product-${product.id}`}>
                {product.image_url ? (
                  <div className="h-48 bg-[#0F172A] flex items-center justify-center overflow-hidden">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                ) : (
                  <div className="h-48 bg-[#0F172A] flex items-center justify-center">
                    <ShoppingBag size={48} weight="thin" className="text-[#D4D4D8]" />
                  </div>
                )}
                <div className="p-4">
                  {product.category && (
                    <div className="flex items-center gap-1 mb-1">
                      <Tag size={12} weight="bold" className="text-[#1D4ED8]" />
                      <span className="text-[10px] uppercase tracking-wider font-bold text-[#1D4ED8]">{product.category}</span>
                    </div>
                  )}
                  <h3 className="text-sm font-bold text-white mb-1">{product.name}</h3>
                  {product.description && (
                    <p className="text-xs text-slate-400 mb-2 line-clamp-2">{product.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mb-2">
                    {product.material && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-[#0F172A] border border-white/10 rounded text-slate-400">{product.material}</span>
                    )}
                    {product.dimensions && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-[#0F172A] border border-white/10 rounded text-slate-400">{product.dimensions}</span>
                    )}
                    {product.finish && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-[#0F172A] border border-white/10 rounded text-slate-400">{product.finish}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-white/10">
                    {product.price > 0 ? (
                      <span className="text-lg font-black text-white">Q{product.price.toFixed(2)}</span>
                    ) : (
                      <span className="text-sm text-slate-400">Consultar precio</span>
                    )}
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                      product.stock === 'No' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'
                    }`}>
                      {product.stock === 'No' ? 'Agotado' : 'Disponible'}
                    </span>
                  </div>
                  {product.notes && (
                    <p className="text-[10px] text-slate-500 mt-2 italic">{product.notes}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer with contact */}
      <div className="bg-[#111111] text-white py-6">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm mb-3">Contactanos para mas informacion</p>
          <div className="flex justify-center gap-3">
            <a
              href="#"
              className="flex items-center gap-2 px-4 py-2 bg-[#0084FF] hover:bg-[#0073E6] rounded-[12px] text-xs font-bold transition-colors"
              data-testid="catalog-messenger-btn"
            >
              <MessengerLogo size={16} weight="fill" /> Messenger
            </a>
            <a
              href="#"
              className="flex items-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#20B858] rounded-[12px] text-xs font-bold transition-colors"
              data-testid="catalog-whatsapp-btn"
            >
              <WhatsappLogo size={16} weight="fill" /> WhatsApp
            </a>
          </div>
          <p className="text-[10px] text-slate-500 mt-4">Powered by Social CRM</p>
        </div>
      </div>
    </div>
  );
};

export default CatalogPublic;
